<template>
  <div class="custom-hours">
    <p class="custom-hours__hint">
      {{ mobile ? $t("custom_schedule_hint_touch") : $t("custom_schedule_hint") }}
    </p>

    <div class="custom-hours__date-row">
      <button type="button" class="custom-hours__nav-btn" @click="shiftDate(-1)">&lsaquo;</button>
      <input type="date" class="custom-hours__date-input" v-model="selectedDate" />
      <button type="button" class="custom-hours__nav-btn" @click="shiftDate(1)">&rsaquo;</button>
    </div>

    <p class="custom-hours__date-summary">{{ summaryForDate(selectedDate) }}</p>

    <div class="custom-hours__toolbar">
      <VBtn size="small" variant="text" color="error" @click="confirmClearSelectedDate">
        {{ $t("clear_day") }}
      </VBtn>
    </div>

    <div ref="gridRef" class="custom-hours__grid">
      <button
        v-for="cell in cellsForSelectedDate"
        :key="cell.minutes"
        type="button"
        :data-minutes="cell.minutes"
        class="custom-hours__cell"
        :class="{
          'custom-hours__cell--open': cell.open,
          'custom-hours__cell--hour': cell.minutes % 60 === 0,
        }"
        @mousedown.prevent="startDrag(cell.minutes)"
        @mouseenter="dragOver(cell.minutes)"
        @mouseup="endDrag"
        @touchstart="onTouchStart(cell.minutes)"
        @touchmove="onTouchMove"
        @touchend="onTouchEnd(cell.minutes)"
      >
        <span v-if="cell.minutes % 60 === 0" class="custom-hours__cell-time">
          {{ minutesToLabel(cell.minutes) }}
        </span>
      </button>
    </div>

    <div class="custom-hours__list" v-if="configuredDates.length">
      <p class="custom-hours__list-title">{{ $t("configured_dates") }}</p>
      <div
        v-for="dateStr in configuredDates"
        :key="dateStr"
        class="custom-hours__list-row"
        :class="{ 'custom-hours__list-row--active': dateStr === selectedDate }"
        @click="selectedDate = dateStr"
      >
        <span class="custom-hours__list-date">{{ formatDate(dateStr) }}</span>
        <span class="custom-hours__list-summary">{{ summaryForDate(dateStr) }}</span>
        <VBtn
          color="red"
          icon
          size="small"
          variant="text"
          @click.stop="confirmClearDate(dateStr)"
        >
          <v-icon size="small">mdi-delete</v-icon>
        </VBtn>
      </div>
    </div>

    <DeleteDialog v-model="clearDialog" @delete="confirmedClear" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useDisplay } from "vuetify";
import { useI18n } from "vue-i18n";
import { WorkingHoursSchedule } from "@/main/types/working-hours-schedule.type";
import DeleteDialog from "@/main/components/delete-popup.vue";

const props = defineProps<{ schedules?: WorkingHoursSchedule[] }>();

const { mobile } = useDisplay();
const { t } = useI18n();

const CELL_MINUTES = Array.from({ length: 96 }, (_, i) => i * 15);

const cellKey = (dateStr: string, minutes: number) => `${dateStr}:${minutes}`;

function pad(n: number) {
  return n.toString().padStart(2, "0");
}

function dateStrOf(d: Date) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function minutesOfDay(d: Date) {
  return d.getHours() * 60 + d.getMinutes();
}

function minutesToLabel(minutes: number) {
  return `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
}

function todayStr() {
  return dateStrOf(new Date());
}

function formatDate(dateStr: string) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

const openSet = ref(new Set<string>());
const originalIds = ref<number[]>([]);
const selectedDate = ref(todayStr());

function loadSchedules(schedules?: WorkingHoursSchedule[]) {
  const set = new Set<string>();
  const ids: number[] = [];
  let earliestDate: string | null = null;

  (schedules ?? []).forEach((s) => {
    if (s._destroy === 1 || !s.custom_available_start || !s.custom_available_end) return;
    if (typeof s.id === "number") ids.push(s.id);

    const start = new Date(s.custom_available_start);
    const end = new Date(s.custom_available_end);
    for (let t = start.getTime(); t < end.getTime(); t += 15 * 60000) {
      const d = new Date(t);
      const dateStr = dateStrOf(d);
      set.add(cellKey(dateStr, minutesOfDay(d)));
      if (earliestDate === null || dateStr < earliestDate) earliestDate = dateStr;
    }
  });

  openSet.value = set;
  originalIds.value = ids;
  if (earliestDate) selectedDate.value = earliestDate;
}

loadSchedules(props.schedules);

const shiftDate = (deltaDays: number) => {
  const [y, m, d] = selectedDate.value.split("-").map(Number);
  selectedDate.value = dateStrOf(new Date(y, m - 1, d + deltaDays));
};

const cellsForSelectedDate = computed(() =>
  CELL_MINUTES.map((minutes) => ({
    minutes,
    open: openSet.value.has(cellKey(selectedDate.value, minutes)),
  }))
);

// Contiguous runs of open 15-minute cells for a date become one schedule
// row each - a gap between runs is a break, and needs no special
// representation of its own.
function deriveRangesForDate(dateStr: string) {
  const openMinutes = CELL_MINUTES.filter((m) =>
    openSet.value.has(cellKey(dateStr, m))
  ).sort((a, b) => a - b);

  const ranges: { opens_at: string; closes_at: string }[] = [];
  let rangeStart: number | null = null;
  let prev: number | null = null;

  openMinutes.forEach((m) => {
    if (rangeStart === null) {
      rangeStart = m;
    } else if (prev !== null && m - prev > 15) {
      ranges.push({ opens_at: minutesToLabel(rangeStart), closes_at: minutesToLabel(prev + 15) });
      rangeStart = m;
    }
    prev = m;
  });
  if (rangeStart !== null && prev !== null) {
    ranges.push({ opens_at: minutesToLabel(rangeStart), closes_at: minutesToLabel(prev + 15) });
  }

  return ranges;
}

const summaryForDate = (dateStr: string) => {
  const ranges = deriveRangesForDate(dateStr);
  if (!ranges.length) return t("closed");
  return ranges.map((r) => `${r.opens_at}–${r.closes_at}`).join(", ");
};

const configuredDates = computed(() => {
  const dates = new Set<string>();
  openSet.value.forEach((key) => dates.add(key.split(":")[0]));
  return Array.from(dates).sort();
});

function setCell(dateStr: string, minutes: number, open: boolean) {
  const key = cellKey(dateStr, minutes);
  if (open) openSet.value.add(key);
  else openSet.value.delete(key);
}

// Mouse drag-to-paint: the first cell touched decides whether the drag
// opens or closes cells (see the identical note in weekly-schedule-calendar.vue).
const dragTargetOpen = ref<boolean | null>(null);

const startDrag = (minutes: number) => {
  const targetOpen = !openSet.value.has(cellKey(selectedDate.value, minutes));
  dragTargetOpen.value = targetOpen;
  setCell(selectedDate.value, minutes, targetOpen);
};

const dragOver = (minutes: number) => {
  if (dragTargetOpen.value === null) return;
  setCell(selectedDate.value, minutes, dragTargetOpen.value);
};

const endDrag = () => {
  dragTargetOpen.value = null;
};

// Touch: a quick tap toggles one cell; press-and-hold-then-drag paints a
// range (see the identical note in weekly-schedule-calendar.vue).
let longPressTimer: number | null = null;
let touchDragging = false;

const clearLongPressTimer = () => {
  if (longPressTimer !== null) {
    window.clearTimeout(longPressTimer);
    longPressTimer = null;
  }
};

const onTouchStart = (minutes: number) => {
  touchDragging = false;
  clearLongPressTimer();
  longPressTimer = window.setTimeout(() => {
    touchDragging = true;
    startDrag(minutes);
  }, 350);
};

const onTouchMove = (event: TouchEvent) => {
  if (!touchDragging) {
    clearLongPressTimer();
    return;
  }
  event.preventDefault();
  const touch = event.touches[0];
  const el = document.elementFromPoint(touch.clientX, touch.clientY) as HTMLElement | null;
  const cellEl = el?.closest<HTMLElement>("[data-minutes]");
  if (cellEl) dragOver(Number(cellEl.dataset.minutes));
};

const onTouchEnd = (minutes: number) => {
  clearLongPressTimer();
  if (touchDragging) {
    endDrag();
  } else {
    setCell(selectedDate.value, minutes, !openSet.value.has(cellKey(selectedDate.value, minutes)));
  }
  touchDragging = false;
};

const onGlobalTouchEnd = () => {
  clearLongPressTimer();
  touchDragging = false;
  endDrag();
};

const clearDialog = ref(false);
const pendingClearDate = ref<string | null>(null);

const confirmClearSelectedDate = () => {
  pendingClearDate.value = selectedDate.value;
  clearDialog.value = true;
};

const confirmClearDate = (dateStr: string) => {
  pendingClearDate.value = dateStr;
  clearDialog.value = true;
};

const confirmedClear = () => {
  if (!pendingClearDate.value) return;
  CELL_MINUTES.forEach((m) =>
    openSet.value.delete(cellKey(pendingClearDate.value!, m))
  );
  pendingClearDate.value = null;
};

// Full-replace save: destroy every row that existed on load and submit the
// current state as entirely new rows (see the identical note in
// weekly-schedule-calendar.vue).
const getScheduleAttributes = (): WorkingHoursSchedule[] => {
  const destroyed: WorkingHoursSchedule[] = originalIds.value.map((id) => ({
    id,
    _destroy: 1,
  }));
  const created: WorkingHoursSchedule[] = configuredDates.value.flatMap((dateStr) =>
    deriveRangesForDate(dateStr).map((range) => {
      const [y, mo, d] = dateStr.split("-").map(Number);
      const [oh, om] = range.opens_at.split(":").map(Number);
      const [ch, cm] = range.closes_at.split(":").map(Number);

      return {
        _destroy: 0,
        custom_available_start: new Date(y, mo - 1, d, oh, om).toISOString(),
        custom_available_end: new Date(y, mo - 1, d, ch, cm).toISOString(),
      };
    })
  );
  return [...destroyed, ...created];
};

const resetFromSchedules = (schedules?: WorkingHoursSchedule[]) => {
  loadSchedules(schedules);
};

defineExpose({ getScheduleAttributes, resetFromSchedules });

const gridRef = ref<HTMLElement>();
let visibilityObserver: ResizeObserver | null = null;

onMounted(() => {
  window.addEventListener("mouseup", endDrag);
  window.addEventListener("touchend", onGlobalTouchEnd);
  window.addEventListener("touchcancel", onGlobalTouchEnd);

  // Scroll past the small hours so business hours are visible right away.
  // This settings tab is kept mounted but `v-show`-hidden while another tab
  // is active, so `scrollHeight` reads 0 until the user actually switches to
  // it - a ResizeObserver reliably catches that hidden-to-visible moment.
  let scrolled = false;
  visibilityObserver = new ResizeObserver(() => {
    const grid = gridRef.value;
    if (scrolled || !grid || !grid.scrollHeight) return;
    scrolled = true;
    const cellHeight = grid.scrollHeight / CELL_MINUTES.length;
    grid.scrollTop = cellHeight * (7 * 4);
    visibilityObserver?.disconnect();
  });
  if (gridRef.value) visibilityObserver.observe(gridRef.value);
});

onUnmounted(() => {
  window.removeEventListener("mouseup", endDrag);
  window.removeEventListener("touchend", onGlobalTouchEnd);
  window.removeEventListener("touchcancel", onGlobalTouchEnd);
  visibilityObserver?.disconnect();
});
</script>

<style lang="scss" scoped>
.custom-hours {
  &__hint {
    color: rgba(0, 0, 0, 0.6);
    font-size: 13px;
    margin-bottom: 8px;
  }

  &__date-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-bottom: 4px;
  }

  &__nav-btn {
    background: none;
    border: none;
    font-size: 22px;
    line-height: 1;
    color: #1f2937;
    cursor: pointer;
    padding: 4px 10px;
    border-radius: 8px;
    min-height: 44px;
    min-width: 44px;

    &:hover {
      background: #f3f4f6;
    }
  }

  &__date-input {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 8px 10px;
  }

  &__date-summary {
    text-align: center;
    font-size: 13px;
    color: #1f2937;
    margin: 0 0 8px;
  }

  &__toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;
  }

  &__grid {
    max-height: min(55vh, 480px);
    overflow-y: auto;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    user-select: none;
    touch-action: pan-y;
  }

  &__cell {
    display: block;
    width: 100%;
    height: 18px;
    padding: 0 10px;
    border: none;
    border-top: 1px solid #f3f4f6;
    background: #fff;
    text-align: left;
    cursor: pointer;

    &--hour {
      border-top: 1px solid #e5e7eb;
      height: 26px;
    }

    &--open {
      background: #e8f5e9;

      &:hover {
        background: #c8e6c9;
      }
    }

    &:hover:not(&--open) {
      background: #f3f4f6;
    }
  }

  &__cell-time {
    font-size: 11px;
    font-weight: 600;
    color: #6b7280;
  }

  &__list {
    margin-top: 16px;
  }

  &__list-title {
    font-size: 13px;
    font-weight: 600;
    color: #6b7280;
    margin-bottom: 6px;
  }

  &__list-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #f3f4f6;
    }

    &--active {
      background: #e3f2fd;
    }
  }

  &__list-date {
    font-weight: 600;
    font-size: 13px;
    color: #1f2937;
    white-space: nowrap;
  }

  &__list-summary {
    font-size: 13px;
    color: #1f2937;
  }
}
</style>
